import { useState } from "react";
import { connect } from "react-redux";

import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import TimelapseRoundedIcon from "@mui/icons-material/TimelapseRounded";
import { Box, Grid, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import { assessmentTemplate as omniHealthHistory } from "../../assets/assessments/omniHealthHistory";
import constants from "../../assets/constants";
import { getAssessmentTemplateByType } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniTextField from "../ui/omniTextField";

const AssessmentSelectionDialog = props => {
  const {
    open,
    onClose,
    onAccept,
    dialogTitle = "Select Assessment",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "sm",
    fullWidth = true,
    organizationODM,
  } = props;

  const theme = useTheme();

  const onAcceptHandler = () => {
    onAccept(selectedAssessmentTemplate);
  };

  const onCloseHandler = () => {
    onClose();
  };

  const createFragmentHTML = text => {
    return { __html: `<Fragment>${text}</Fragment>` };
  };

  const getSupportedAssessmentTemplates = orgODM => {
    if (orgODM != null) {
      let theReturn = [];
      for (let index = 0; index < orgODM.omniData.supportedAssessmentTypes.length; ++index) {
        let aTemplate = getAssessmentTemplateByType(
          orgODM.omniData.supportedAssessmentTypes[index],
        );

        if (aTemplate) theReturn.push(aTemplate);
      }
      if (theReturn.length === 0) {
        let aTemplate = getAssessmentTemplateByType(constants.assessmentIDs.botpressDefault);
        theReturn.push(aTemplate);
      }
      return theReturn;
    }

    return [omniHealthHistory];
  };

  const [myAssessmentTemplates] = useState(getSupportedAssessmentTemplates(organizationODM));

  const [selectedAssessmentTemplate, setSelectedAssessmentTemplate] = useState(
    myAssessmentTemplates[0],
  );

  return (
    <Dialog
      open={open}
      onClose={() => onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid item xs={12}>
          <OmniTextField
            label="Select Assessment"
            select
            key={uuid()}
            name={uuid()} //prevents AutoComplete Suggestions
            variant="outlined"
            value={
              !selectedAssessmentTemplate
                ? "Select Assessment"
                : selectedAssessmentTemplate.omniData.title
            }
            onChange={event => {
              setSelectedAssessmentTemplate(
                myAssessmentTemplates.find(({ omniData }) => omniData.title === event.target.value),
              );
            }}
          >
            {myAssessmentTemplates.map(assessmentTemplate => (
              <OmniMenuItem
                key={assessmentTemplate.omniData.title}
                value={assessmentTemplate.omniData.title}
              >
                {assessmentTemplate.omniData.title}
              </OmniMenuItem>
            ))}
          </OmniTextField>
        </Grid>
        {/* Icons and Such */}
        <Grid item xs={12} padding={1}>
          <Box display={"flex"} flexDirection={"row"}>
            <QuestionAnswerRoundedIcon
              fontSize="large"
              sx={{ marginRight: theme.spacing(0.5), marginBottom: "-5px" }}
            />
            <Typography variant="h6" maxWidth={"300px"}>{`${
              selectedAssessmentTemplate.omniData.questions.length
            } Question${
              selectedAssessmentTemplate.omniData.questions.length > 1 ? `s` : ``
            }`}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} padding={1}>
          <Box display={"flex"} flexDirection={"row"}>
            <TimelapseRoundedIcon
              fontSize="large"
              sx={{ marginRight: theme.spacing(0.5), marginBottom: "-5px" }}
            />
            <Typography variant="h6">{selectedAssessmentTemplate.omniData.duration}</Typography>
          </Box>
        </Grid>

        {selectedAssessmentTemplate.omniData.description && (
          <Box sx={{ display: "flex", width: "100%", flexDirection: "column" }}>
            <Typography variant="overline" noWrap sx={{ marginBottom: `-${theme.spacing(1)}` }}>
              description
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: "1.2rem",
                marginBottom: theme.spacing(1),
                marginLeft: theme.spacing(0.5),
              }}
              dangerouslySetInnerHTML={createFragmentHTML(
                selectedAssessmentTemplate.omniData.description,
              )}
            />
          </Box>
        )}
        {selectedAssessmentTemplate.omniData.directions && (
          <Box sx={{ display: "flex", width: "100%", flexDirection: "column" }}>
            <Typography variant="overline" noWrap sx={{ marginBottom: `-${theme.spacing(1)}` }}>
              directions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: "1.2rem",
                marginBottom: theme.spacing(1),
                marginLeft: theme.spacing(0.5),
              }}
              dangerouslySetInnerHTML={createFragmentHTML(
                selectedAssessmentTemplate.omniData.directions,
              )}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {cancelMessage && (
          <Button onClick={onCloseHandler} color="secondary" variant="contained">
            {cancelMessage}
          </Button>
        )}
        <Button onClick={onAcceptHandler} color="secondary" variant="contained">
          {okMessage}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  organizationODM: state.userSession.organizationODM,
  assessmentTemplates: state.assessmentSession.assessmentTemplates,
  assessmentTemplateODM: state.assessmentSession.assessmentTemplateODM,
});
export default connect(mapStateToProps)(AssessmentSelectionDialog);
